import React, { useState } from "react";
import * as Icons from "lucide-react";
import { STATUS } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { AREAS_ARCHIVO, moduloDeAsunto } from "@/lib/archivo";
import { MatterCard, CompactMatterRow } from "@/components/MatterCard";

const RANGO = { [STATUS.ATTENTION]: 0, [STATUS.UPCOMING]: 1, [STATUS.CONTROLLED]: 2 };
const KEY = "aldia-asuntos-abiertos";
const leer = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };

// Estado → vencimiento más próximo → más reciente.
const ordenar = (a, b) =>
  (RANGO[a.status] ?? 3) - (RANGO[b.status] ?? 3) ||
  (a.due_date && b.due_date ? a.due_date.localeCompare(b.due_date) : a.due_date ? -1 : b.due_date ? 1 : 0) ||
  (b.created_at || "").localeCompare(a.created_at || "");

const Resumen = ({ items }) => {
  const n = (st) => items.filter((m) => m.status === st).length;
  const partes = [
    [n(STATUS.ATTENTION), "atención", "text-[#B91C1C]"],
    [n(STATUS.UPCOMING), n(STATUS.UPCOMING) === 1 ? "próximo" : "próximos", "text-[#B45309]"],
    [n(STATUS.CONTROLLED), "al día", "text-[#15803D]"],
  ].filter(([c]) => c > 0);
  return (
    <span className="text-sm font-semibold">
      {partes.map(([c, t, cls], i) => <span key={t} className={cls}>{i > 0 && <span className="text-[#9AA39D]"> · </span>}{c} {t}</span>)}
    </span>
  );
};

const Bloque = ({ area, items, abierto, onToggle }) => {
  const I = Icons[area.icon] || Icons.Folder;
  return (
    <section data-testid={`modulo-${area.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl overflow-hidden">
      <button data-testid={`modulo-${area.id}-toggle`} onClick={onToggle} aria-expanded={abierto}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#FAF8F5] transition-colors">
        <span className="w-11 h-11 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><I size={22} className="text-[#3A6B56]" /></span>
        <span className="flex-1 min-w-0">
          <span className="block font-heading font-bold text-lg text-[#1C2421] leading-tight">
            {area.label} <span className="text-[#6E7A74]" data-testid={`modulo-${area.id}-count`}>· {items.length}</span>
          </span>
          <span data-testid={`modulo-${area.id}-resumen`}><Resumen items={items} /></span>
        </span>
        <Icons.ChevronDown size={22} className={`text-[#6E7A74] shrink-0 transition-transform duration-200 ${abierto ? "rotate-180" : ""}`} />
      </button>
      {abierto && (
        <div className="px-3 pb-3 space-y-2.5" data-testid={`modulo-${area.id}-lista`}>
          {items.map((m, i) => (m.status === STATUS.CONTROLLED
            ? <CompactMatterRow key={m.id} matter={m} index={i} />
            : <MatterCard key={m.id} matter={m} index={i} />))}
        </div>
      )}
    </section>
  );
};

// «Tus asuntos» agrupados por módulo; cada bloque se pliega y se recuerda en este dispositivo.
export const MatterModulos = ({ matters, emptyText }) => {
  const data = useData();
  const [abiertos, setAbiertos] = useState(leer);
  const grupos = AREAS_ARCHIVO.map((a) => ({ area: a, items: matters.filter((m) => moduloDeAsunto(m, data) === a.id).sort(ordenar) }))
    .filter((g) => g.items.length > 0);
  if (!grupos.length) {
    return <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-2xl p-6 text-center" data-testid="asuntos-vacio">{emptyText}</p>;
  }
  const toggle = (id, abierto) => {
    const n = { ...abiertos, [id]: abierto };
    setAbiertos(n);
    localStorage.setItem(KEY, JSON.stringify(n));
  };
  return (
    <div className="space-y-3" data-testid="asuntos-modulos">
      {grupos.map(({ area, items }) => {
        const porDefecto = items.some((m) => m.status !== STATUS.CONTROLLED);
        const abierto = abiertos[area.id] ?? porDefecto;
        return <Bloque key={area.id} area={area} items={items} abierto={abierto} onToggle={() => toggle(area.id, !abierto)} />;
      })}
    </div>
  );
};
